import { BaseAdapter } from "./base-adapter";
import {
  TEvent,
  EventType,
  IPageViewEvent,
  IFormSubmissionEvent,
  IProductViewEvent,
  IAddToCartEvent,
  ICheckoutStartedEvent,
  ICheckoutCompletedEvent,
  ISearchEvent,
  ICustomEvent,
  IAddPaymentInfoEvent,
} from "../types";

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

/**
 * Configuration for the Google Analytics adapter
 */
export interface GoogleAnalyticsAdapterConfig {
  /**
   * Google Analytics measurement ID (e.g., G-XXXXXXXXXX)
   */
  measurementId: string;
}

/**
 * Google Analytics tracking adapter
 * Assumes that the Google Analytics script is already loaded in the page
 */
export class GoogleAnalyticsAdapter extends BaseAdapter {
  public readonly name = "google-analytics";

  /**
   * Constructor
   * @param config Configuration for the adapter
   */
  constructor(config: GoogleAnalyticsAdapterConfig) {
    super(config);
  }

  /**
   * Initialize Google Analytics adapter
   * This just checks if gtag is available
   */
  public async initialize(): Promise<void> {
    if (this.initialized) {
      console.log("GA4: Already initialized");
      return;
    }

    // Skip initialization if running on server
    if (typeof window === "undefined") {
      console.log("GA4: Skipping initialization on server");
      return;
    }

    // Check if gtag is available
    if (!window.gtag) {
      console.warn("GA4: Google Analytics gtag not found. Make sure the script is loaded.");
      return;
    }

    console.log("GA4: Successfully initialized");
    this.initialized = true;
  }

  /**
   * Track an event with Google Analytics
   * @param event The event to track
   */
  public async trackEvent(event: TEvent): Promise<void> {
    if (!this.initialized || typeof window === "undefined" || !window.gtag) {
      console.warn(
        "GA4: Cannot track event, adapter not initialized or gtag not available",
        event.type
      );
      return;
    }

    const measurementId = this.getConfig<string>("measurementId");
    console.log(`GA4: Tracking event ${event.type}`);

    switch (event.type) {
      case EventType.PAGE_VIEW:
        this.trackPageView(event as IPageViewEvent, measurementId);
        break;
      case EventType.USER_ENGAGEMENT:
        this.trackUserEngagement();
        break;
      case EventType.SESSION_START:
        this.trackSessionStart();
        break;
      case EventType.FIRST_VISIT:
        this.trackFirstVisit();
        break;
      case EventType.PRODUCT_VIEW:
        this.trackProductView(event as IProductViewEvent);
        break;
      case EventType.SCROLL:
        this.trackScroll();
        break;
      case EventType.ADD_TO_CART:
        this.trackAddToCart(event as IAddToCartEvent);
        break;
      case EventType.FORM_START:
        this.trackFormStart();
        break;
      case EventType.CHECKOUT_STARTED:
        this.trackCheckoutStarted(event as ICheckoutStartedEvent);
        break;
      case EventType.FORM_SUBMISSION:
        this.trackFormSubmission(event as IFormSubmissionEvent);
        break;
      case EventType.SEARCH:
        this.trackSearch(event as ISearchEvent);
        break;
      case EventType.CHECKOUT_COMPLETED:
        this.trackCheckoutCompleted(event as ICheckoutCompletedEvent);
        break;
      case EventType.ADD_PAYMENT_INFO:
        this.trackAddPaymentInfo(event as IAddPaymentInfoEvent);
        break;
      case EventType.CUSTOM:
        this.trackCustomEvent(event as ICustomEvent);
        break;

      default:
        // For other events, track as custom events
        const params = this.eventToParams(event);
        window.gtag("event", event.type, this.enhanceWithAffiliateParams(params));
        break;
    }
  }

  /**
   * Track a page view event
   * @param event The page view event
   * @param measurementId The Google Analytics measurement ID
   */
  private trackPageView(event: IPageViewEvent, measurementId: string): void {
    const params = {
      page_path: event.path,
      page_title: event.title,
      page_location: window.location.href,
      page_referrer: document.referrer || undefined,
      ga_session_id: "", // Placeholder
      ga_session_number: "", // Placeholder
      gclid: "",
      engagement_time_msec: undefined,
      campaign: "",
      campaign_id: "",
      content: "",
      debug_mode: undefined,
      medium: "",
      source: "",
      page_term: "",
      ignore_referrer: undefined,
      batch_ordering_id: "",
      batch_page_id: "",
    };
    
    window.gtag!("event", 'page_view', this.enhanceWithAffiliateParams(params));
  }

  private trackUserEngagement(): void {
    const params = {
      // Parameters from the image
      batch_ordering_id: "",
      batch_page_id: "",
      campaign: "",
      ga_session_id: "",
      ga_session_number: "",
      gclid: "",
      ignore_referrer: "",
      medium: "",
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_referrer: document.referrer,
      page_title: document.title,
      source: "",
      srsltid: "",
      term: "",
    };
    
    window.gtag!("event", "user_engagement", this.enhanceWithAffiliateParams(params));
  }

  private trackSessionStart(): void {
    const params = {
      // Parameters from the image
      batch_ordering_id: "",
      batch_page_id: "",
      campaign: "",
      campaign_id: "",
      content: "",
      ga_session_id: "",
      ga_session_number: "",
      gclid: "",
      ignore_referrer: "",
      medium: "",
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_referrer: document.referrer,
      page_title: document.title,
      source: "",
      srsltid: "",
      term: "",
    };
    
    window.gtag!("event", "session_start", this.enhanceWithAffiliateParams(params));
  }

  private trackFirstVisit(): void {
    const params = {
      batch_ordering_id: "",
      batch_page_id: "",
      campaign: "",
      campaign_id: "",
      content: "",
      debug_mode: "",
      engagement_time_msec: 0,
      entrances: 1,
      ga_session_id: "",
      ga_session_number: "",
      gclid: "",
      ignore_referrer: "",
      medium: "",
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_referrer: document.referrer,
      page_title: document.title,
      page_source: "",
      page_term: "",
    };
    
    window.gtag!("event", "first_visit", this.enhanceWithAffiliateParams(params));
  }

  private trackProductView(event: IProductViewEvent): void {
    const params = {
      batch_ordering_id: "",
      batch_page_id: "",
      campaign: "",
      ecomm_pagetype: "",
      ecomm_prodid: event.productId,
      ecomm_totalvalue: event.price,
      engagement_time_msec: undefined,
      ga_session_id: "",
      ga_session_number: "",
      gclid: "",
      ignore_referrer: undefined,
      medium: "",
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_referrer: document.referrer,
      page_title: document.title,
      source: "",
      srtsltid: "",
      term: "",
      items: [
        {
          item_id: event.productId,
          item_name: event.productName,
          price: event.price,
          item_category: event.category,
        },
      ],
    };
    
    window.gtag!("event", "view_item", this.enhanceWithAffiliateParams(params));
  }

  private trackScroll(): void {
    const params = {
      batch_ordering_id: "",
      batch_page_id: "",
      engagement_time_msec: undefined,
      ga_session_id: "",
      ga_session_number: "",
      ignore_referrer: undefined,
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_referrer: document.referrer,
      page_title: document.title,
      percent_scrolled: 90, // or dynamically captured
    };
    
    window.gtag!("event", "scroll", this.enhanceWithAffiliateParams(params));
  }

  private trackAddToCart(event: IAddToCartEvent): void {
    const params = {
      batch_ordering_id: "",
      batch_page_id: "",
      currency: event.currency || "INR",
      ecomm_pagetype: "",
      ecomm_prodid: event.productId,
      ecomm_totalvalue: event.price,
      value: event.price,
      engagement_time_msec: undefined,
      ga_session_id: "",
      ga_session_number: "",
      ignore_referrer: undefined,
      page_location: window.location.href,
      page_referrer: document.referrer,
      page_title: document.title,
      items: [
        {
          item_id: event.productId,
          item_name: event.productName,
          price: event.price,
          item_variant: event.variant,
        },
      ],
    };
    
    window.gtag!("event", "add_to_cart", this.enhanceWithAffiliateParams(params));
  }

  private trackFormStart(): void {
    const params = {
      batch_ordering_id: "",
      batch_page_id: "",
      engagement_time_msec: undefined,
      first_field_id: "",
      first_field_name: "",
      first_field_position: "",
      first_field_type: "",
      form_destination: "",
      form_length: "",
      form_id: "",
      ga_session_id: "",
      ga_session_number: "",
      ignore_referrer: undefined,
      page_location: window.location.href,
      page_referrer: document.referrer,
      page_title: document.title,
    };
    
    window.gtag!("event", "form_start", this.enhanceWithAffiliateParams(params));
  }

  private trackCheckoutStarted(event: ICheckoutStartedEvent): void {
    const params = {
      batch_ordering_id: "",
      batch_page_id: "",
      currency: event.currency || "INR",
      ecomm_pagetype: "",
      ecomm_prodid: "",
      ecomm_totalvalue: event.cartValue,
      value: event.cartValue,
      engagement_time_msec: undefined,
      ga_session_id: "",
      ga_session_number: "",
      ignore_referrer: undefined,
      page_location: window.location.href,
      page_referrer: document.referrer,
      page_title: document.title,
      items: event.items.map((item) => ({
        item_id: item.productId,
        item_name: item.productName,
        price: item.price,
        quantity: item.quantity,
        item_variant: item.variant,
      })),
    };
    
    window.gtag!("event", "begin_checkout", this.enhanceWithAffiliateParams(params));
  }

  private trackFormSubmission(event: IFormSubmissionEvent): void {
    const params = {
      batch_ordering_id: "",
      batch_page_id: "",
      engagement_time_msec: undefined,
      form_destination: "",
      form_length: "",
      form_id: event.formId,
      ga_session_id: "",
      ga_session_number: "",
      ignore_referrer: undefined,
      page_location: window.location.href,
      page_referrer: document.referrer,
      page_title: document.title,
    };
    
    window.gtag!("event", "form_submit", this.enhanceWithAffiliateParams(params));
  }

  private trackSearch(event: ISearchEvent): void {
    const params = {
      batch_ordering_id: "",
      batch_page_id: "",
      engagement_time_msec: undefined,
      ga_session_id: "",
      ga_session_number: "",
      ignore_referrer: undefined,
      page_location: window.location.href,
      page_referrer: document.referrer,
      page_title: document.title,
      search_term: event.searchTerm,
    };
    
    window.gtag!("event", "search", this.enhanceWithAffiliateParams(params));
  }

  /**
   * Track a checkout completed event
   * @param event The checkout completed event
   */
  private trackCheckoutCompleted(event: ICheckoutCompletedEvent): void {
    const params = {
      transaction_id: event.orderId,
      currency: event.currency || "INR",
      value: event.cartValue,
      items: event.items.map((item) => ({
        item_id: item.productId,
        item_name: item.productName,
        price: item.price,
        quantity: item.quantity,
        item_variant: item.variant,
      })),
    };
    
    window.gtag!("event", "purchase", this.enhanceWithAffiliateParams(params));
  }

  /**
   * Track a custom event
   * @param event The custom event
   */
  private trackCustomEvent(event: ICustomEvent): void {
    const params = event.properties || {};
    window.gtag!("event", event.name, this.enhanceWithAffiliateParams(params));
  }

  /**
   * Track an add payment info event
   * @param event The add payment info event
   */
  private trackAddPaymentInfo(event: IAddPaymentInfoEvent): void {
    const params = {
      currency: event.currency || "INR",
      value: event.cartValue,
      payment_type: event.paymentType || "",
      items: event.items.map((item) => ({
        item_id: item.productId,
        item_name: item.productName,
        price: item.price,
        quantity: item.quantity,
        item_variant: item.variant,
      })),
    };
    
    window.gtag!("event", "add_payment_info", this.enhanceWithAffiliateParams(params));
  }

  /**
   * Convert an event to Google Analytics parameters
   * @param event The event to convert
   */
  private eventToParams(event: TEvent): Record<string, any> {
    // Remove type and timestamp from the event
    const { type, timestamp, ...params } = event;
    return this.enhanceWithAffiliateParams(params);
  }
}
